import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import {
  useErrors,
  useSignupIdentifiers,
  useUsernameValidation,
} from "@auth0/auth0-acul-react/signup-id";
import type {
  ErrorItem,
  IdentifierType,
  SignupOptions,
  UsernameValidationResult,
} from "@auth0/auth0-acul-react/types";

import Captcha from "@/components/Captcha/index";
import RequiredFooter from "@/components/RequiredFooter";
import { ULThemeCheckbox } from "@/components/ULThemeCheckbox";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import ULThemeCountryCodePicker from "@/components/ULThemeCountryCodePicker";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import { useCaptcha } from "@/hooks/useCaptcha";
import { transformAuth0CountryCode } from "@/utils/helpers/countryUtils";
import { getIndividualIdentifierDetails } from "@/utils/helpers/identifierUtils";
import { createUsernameValidator } from "@/utils/validations";

import { useSignupIdManager } from "../hooks/useSignupIdManager";

function SignupIdForm() {
  const {
    transaction,
    handleSignup,
    handlePickCountryCode,
    isCaptchaAvailable,
    texts,
    captcha,
    locales,
  } = useSignupIdManager();

  const { errors, hasError, dismiss } = useErrors();

  // Privacy-consent checkbox is a pure client-side gate (Continue disabled
  // until ticked) — it is deliberately NOT submitted as a ulp-* field and is
  // not read by any Action. Distinct from ulp-consent, which is the step-5
  // declaration on signup-password. See ARCHITECTURE.md "Member Signup Flow".
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const form = useForm<SignupOptions>({
    defaultValues: {
      email: "",
      username: "",
      phone: "",
      captcha: "",
    },
    reValidateMode: "onBlur",
  });

  const {
    formState: { isSubmitting },
    watch,
  } = form;

  // Get username validation
  const userNameValue = watch("username");
  const {
    isValid: isUsernameValid,
    errors: userNameErrors,
  }: UsernameValidationResult = useUsernameValidation(userNameValue || "");

  // Get identifiers from transaction
  const enabledIdentifiers = useSignupIdentifiers();

  // Extract required and optional identifiers from the hook data
  const requiredIdentifiers = useMemo(
    () =>
      (enabledIdentifiers || [])
        .filter((identifier) => identifier.required)
        .map((identifier) => identifier.type),
    [enabledIdentifiers]
  );

  // `username` is deliberately suppressed here, not at the connection.
  //
  // The database connection has username as an active login identifier with
  // signup.status = "optional" (see infra/terraform/auth0.tf) — that pairing
  // is what lets a member log in with their member number, and "inactive" is
  // rejected outright by Auth0, so the field cannot be turned off there.
  // Auth0's screen context therefore offers it as an optional signup field
  // and this component would otherwise render it as "Username (optional)".
  //
  // It must not be shown: the member number is minted by a Postgres sequence
  // and written to the Auth0 username by the relay Action after signup, so
  // anything a member typed here would be silently overwritten. An input
  // that is quietly discarded is worse than no input, and the real CareSuper
  // join journey this flow replicates has no such field.
  //
  // Filtered by type rather than by dropping the renderFields call below, so
  // any future optional identifier still renders.
  const optionalIdentifiers = useMemo(
    () =>
      (enabledIdentifiers || [])
        .filter((identifier) => !identifier.required)
        .map((identifier) => identifier.type)
        .filter((type) => type !== "username"),
    [enabledIdentifiers]
  );

  // Use locale strings with fallback to SDK texts
  const buttonText = texts?.buttonText || locales.form.button;
  const captchaLabel = texts?.captchaCodePlaceholder
    ? `${texts.captchaCodePlaceholder}*`
    : `${locales.form.fields.captcha.label}*`;

  // Setup captcha with useCaptcha hook.
  //
  // `captchaValue` and `isCaptchaSolved` MUST be consumed here — they are the
  // only route the solved token has out of the widget. The widget providers
  // for auth0_v2/hcaptcha/recaptcha report their token through
  // `onCaptchaResponse` -> useCaptcha, and are NOT registered as react-hook-form
  // fields (only SimpleCaptchaWidget, the auth0 v1 image captcha, is given
  // `control`/`rules` by Captcha/index.tsx). So `data.captcha` stays "" on
  // submit no matter how the member solves the challenge, and dropping these
  // two values makes signup fail with `invalid-recaptcha` on every attempt.
  const { captchaConfig, captchaProps, captchaValue, isCaptchaSolved } =
    useCaptcha(captcha || undefined, captchaLabel);

  // Same condition that renders the widget below — gating on `isCaptchaSolved`
  // alone would deadlock the button if a captcha config ever arrived while
  // `isCaptchaAvailable` was false, since the widget that resolves it would
  // never render.
  const captchaRequired = isCaptchaAvailable && !!captchaConfig;

  // Get general errors (not field-specific)
  const generalErrors: ErrorItem[] = errors
    .byType("auth0")
    .filter((err) => !err.field);

  const captchaSDKError = errors.byField("captcha")[0]?.message;

  const onSubmit = async (data: SignupOptions) => {
    // `captchaValue` overrides `data.captcha`: for every widget provider the
    // form field is unregistered and therefore still the "" default, so the
    // hook's value is the only real token. For the auth0 v1 image captcha both
    // carry the same answer.
    await handleSignup({ ...data, captcha: captchaValue ?? data.captcha });
  };

  const renderIdentifierField = (
    identifierType: IdentifierType,
    isRequired: boolean
  ) => {
    const { label, type, autoComplete } = getIndividualIdentifierDetails(
      identifierType,
      isRequired,
      texts
    );

    const sdkError = errors.byField(identifierType)[0]?.message;

    return (
      <FormField
        key={identifierType}
        control={form.control}
        name={identifierType}
        rules={{
          required: isRequired ? locales.form.fields.common.required : false,
          ...(identifierType === "username" && {
            validate: createUsernameValidator(
              isUsernameValid,
              userNameErrors,
              isRequired,
              locales.form.fields.common.required
            ),
          }),
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={label}
              type={type}
              autoComplete={autoComplete}
              error={!!fieldState.error || !!sdkError}
            />
            <ULThemeFormMessage
              sdkError={sdkError}
              hasFormError={!!fieldState.error}
            />
          </FormItem>
        )}
      />
    );
  };

  const renderFields = (identifiers: IdentifierType[], isRequired: boolean) =>
    identifiers.map((identifierType) => {
      if (identifierType === "phone") {
        const phoneCountryCode = transformAuth0CountryCode(
          transaction?.countryCode,
          transaction?.countryPrefix
        );

        return (
          <div
            key={`${isRequired ? "required" : "optional"}-phone-container`}
            className="space-y-2"
          >
            <ULThemeCountryCodePicker
              selectedCountry={phoneCountryCode}
              onClick={handlePickCountryCode}
              fullWidth
              placeholder={locales.form.fields.countryCode.placeholder}
            />
            {renderIdentifierField(identifierType, isRequired)}
          </div>
        );
      }
      return renderIdentifierField(identifierType, isRequired);
    });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {/* General alerts at the top */}
        {hasError && generalErrors.length > 0 && (
          <div className="space-y-3 mb-4">
            {generalErrors.map((error) => (
              <ULThemeAlert
                key={error.id}
                variant="destructive"
                onDismiss={() => dismiss(error.id)}
              >
                <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
              </ULThemeAlert>
            ))}
          </div>
        )}

        {/* Required identifier fields (email only, once username/phone are
            disabled on the connection) */}
        {renderFields(requiredIdentifiers, true)}

        {/* Optional identifier fields */}
        {renderFields(optionalIdentifiers, false)}

        {/* Privacy consent — client-side gate only, see comment above */}
        <div className="flex items-start gap-2 mt-2 mb-2 p-3 rounded-lg" style={{ backgroundColor: "#E6F7F1" }}>
          <ULThemeCheckbox
            checked={privacyConsent}
            onCheckedChange={(checked) => setPrivacyConsent(!!checked)}
            id="privacy-consent"
          />
          <label htmlFor="privacy-consent" className="text-sm theme-universal:text-body-text">
            I agree to the{" "}
            <a href="#privacy-policy" className="underline" style={{ color: "#4A44F2" }}>
              Privacy Policy
            </a>
            .
          </label>
        </div>

        {/* CAPTCHA Box */}
        {isCaptchaAvailable && captchaConfig && (
          <Captcha
            control={form.control}
            name="captcha"
            captcha={captchaConfig}
            {...captchaProps}
            sdkError={captchaSDKError}
            rules={{
              required: locales.form.fields.captcha.required,
            }}
          />
        )}

        {/* Submit button */}
        <ULThemeButton
          type="submit"
          className="w-full"
          disabled={
            isSubmitting ||
            !privacyConsent ||
            (captchaRequired && !isCaptchaSolved)
          }
        >
          {buttonText}
        </ULThemeButton>
        <RequiredFooter />
      </form>
    </Form>
  );
}

export default SignupIdForm;
